import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TransferService from "../services/TransferService";
import Labels from "../labels/Labels";
import HttpService from "../services/HttpService";
import RenderOnRole from "./RenderOnRole";
import SvbTransferResolutionDialog from "./SvbTransferResolutionDialog";

async function fetchEmployers() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + "/employer/all?excludeRelations=true"
  );
  return response.data;
}

function initialDates() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 29);
  const format = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
  return { from: format(start), to: format(today) };
}

export default function SvbTransfers({ employerId }) {
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const needsSelection = !employerId || employerId === "ANY";
  const {
    data: employers,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery(["svb-transfer-employers", employerId], fetchEmployers, {
    enabled: needsSelection,
    retry: false,
  });
  const effectiveEmployerId = needsSelection
    ? selectedEmployer?.idEmployer
    : employerId;

  return (
    <Box>
      {needsSelection && (
        <Box sx={{ my: 2 }}>
          <Autocomplete
            sx={{ maxWidth: 500 }}
            options={(employers || []).filter(
              (employer) => employer.idEmployer && employer.idEmployer !== "ANY"
            )}
            loading={isFetching}
            value={selectedEmployer}
            onChange={(_event, employer) => setSelectedEmployer(employer)}
            isOptionEqualToValue={(option, value) =>
              option.idEmployer === value.idEmployer
            }
            getOptionLabel={(employer) =>
              `${employer.name || employer.description || employer.idEmployer} [${employer.idEmployer}]`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={Labels.getLabel(
                  "queryTransfers.svb.employer",
                  "Employer"
                )}
              />
            )}
            noOptionsText={Labels.getLabel(
              "queryTransfers.svb.noEmployers",
              "No employers available"
            )}
          />
          {isError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Button onClick={() => refetch()}>
                  {Labels.getLabel("queryTransfers.svb.retry", "Retry")}
                </Button>
              }
            >
              {Labels.getLabel(
                "queryTransfers.svb.employerError",
                "Error loading employers"
              )}
              {": "}
              {error?.response?.data?.description || error?.message}
            </Alert>
          )}
        </Box>
      )}
      <SvbTransferResults
        key={effectiveEmployerId || "unselected"}
        employerId={effectiveEmployerId}
      />
    </Box>
  );
}

function SvbTransferResults({ employerId }) {
  const [dates, setDates] = useState(initialDates);
  const [searchDates, setSearchDates] = useState(dates);
  const [pageSize, setPageSize] = useState(10);
  const [validationError, setValidationError] = useState(false);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [resolution, setResolution] = useState(null);
  const hasEmployer = !!employerId && employerId !== "ANY";
  const { data, isFetching, isError, error, refetch } = useQuery(
    ["svb-transfers", employerId, searchDates.from, searchDates.to],
    () =>
      TransferService.fetchSvbTransfers(
        employerId,
        searchDates.from,
        searchDates.to
      ),
    { enabled: hasEmployer, keepPreviousData: false, retry: false }
  );

  const search = (event) => {
    event.preventDefault();
    if (!dates.from || !dates.to || dates.from > dates.to) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    if (dates.from === searchDates.from && dates.to === searchDates.to) {
      refetch();
    } else {
      setSearchDates({ ...dates });
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: Labels.getLabel("queryTransfers.column.actions", "Actions"),
      width: 150,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          {!row.processed && (
            <RenderOnRole roles={["transfers-confirm"]}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip
                  title={Labels.getLabel(
                    "queryTransfers.svb.markManual",
                    "Mark MANUAL PROCESSED"
                  )}
                >
                  <Button
                    style={{ minWidth: "10px" }}
                    color="success"
                    aria-label={Labels.getLabel(
                      "queryTransfers.svb.markManual",
                      "Mark MANUAL PROCESSED"
                    )}
                    onClick={() =>
                      setResolution({ row, action: "MANUAL_PROCESSED" })
                    }
                  >
                    <CheckCircleOutlineIcon />
                  </Button>
                </Tooltip>
                <Tooltip
                  title={Labels.getLabel(
                    "queryTransfers.svb.mapTitle",
                    "Map studies & create transfers"
                  )}
                >
                  <span>
                    <Button
                      style={{ minWidth: "10px" }}
                      color="primary"
                      aria-label={Labels.getLabel(
                        "queryTransfers.svb.mapTitle",
                        "Map studies & create transfers"
                      )}
                      disabled={!row.studies?.length}
                      onClick={() => setResolution({ row, action: "MAPPED" })}
                    >
                      <AccountTreeIcon />
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </RenderOnRole>
          )}
        </Box>
      ),
    },
    {
      field: "asOfDate",
      headerName: Labels.getLabel(
        "queryTransfers.svb.dateProcessing",
        "Date / Processing"
      ),
      width: 200,
      valueGetter: ({ row }) =>
        [
          row.asOfDate,
          row.resolution === "MANUAL_PROCESSED"
            ? Labels.getLabel(
                "queryTransfers.svb.manualProcessed",
                "MANUAL PROCESSED"
              )
            : row.processed
              ? Labels.getLabel("queryTransfers.svb.processed", "Processed")
              : Labels.getLabel(
                  "queryTransfers.svb.notProcessed",
                  "Not processed"
                ),
        ]
          .filter(Boolean)
          .join(" / "),
      renderCell: ({ row }) => (
        <Box sx={{ py: 1, whiteSpace: "normal" }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {row.asOfDate}
          </Typography>
          <Chip
            size="small"
            variant={row.processed ? "filled" : "outlined"}
            color={row.processed ? "success" : "default"}
            label={
              row.resolution === "MANUAL_PROCESSED"
                ? Labels.getLabel(
                    "queryTransfers.svb.manualProcessed",
                    "MANUAL PROCESSED"
                  )
                : row.processed
                  ? Labels.getLabel("queryTransfers.svb.processed", "Processed")
                  : Labels.getLabel(
                      "queryTransfers.svb.notProcessed",
                      "Not processed"
                    )
            }
          />
          {row.resolvedBy && (
            <Typography
              variant="caption"
              component="div"
              title={row.resolutionReason}
            >
              {row.resolvedBy} · {row.resolvedAt} UTC
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "id",
      headerName: Labels.getLabel(
        "queryTransfers.svb.transactionSender",
        "SVB Transaction ID / Sender"
      ),
      width: 300,
      valueGetter: ({ row }) =>
        [row.id, row.senderName, row.bankReference].filter(Boolean).join(" / "),
      renderCell: ({ row }) => (
        <Box sx={{ py: 1, whiteSpace: "normal", overflowWrap: "anywhere" }}>
          <Typography variant="body2">{row.id}</Typography>
          {row.senderName && (
            <Typography variant="body2" color="text.secondary">
              {row.senderName}
            </Typography>
          )}
          {row.bankReference && (
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
            >
              {Labels.getLabel(
                "queryTransfers.svb.reference",
                "Bank Reference"
              )}
              : {row.bankReference}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: Labels.getLabel("queryTransfers.column.amount", "Amount"),
      width: 150,
      valueFormatter: ({ value, api, id }) =>
        [value, api.getRow(id)?.currency]
          .filter((part) => part != null && part !== "")
          .join(" "),
      renderCell: ({ row }) => (
        <Box sx={{ py: 1, whiteSpace: "normal" }}>
          <Typography variant="body2" component="span">
            {row.amount}
          </Typography>
          {row.currency && (
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              {row.currency}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "studies",
      headerName: Labels.getLabel(
        "queryTransfers.svb.studies",
        "Study breakdown"
      ),
      width: 520,
      sortable: false,
      filterable: false,
      valueFormatter: ({ value }) =>
        (value || [])
          .map(
            (study) =>
              `${study.studyId || ""}: ${study.amount ?? ""} (${study.invoiceNumber || ""})`
          )
          .join("; "),
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            py: 1,
            whiteSpace: "normal",
            "& .MuiTableCell-root": {
              fontSize: "0.75rem",
              lineHeight: 1.4,
              px: 1,
              py: 0.5,
              overflowWrap: "anywhere",
            },
            "& .MuiAlert-message": { fontSize: "0.75rem" },
          }}
        >
          {row.studyDetailsWarning && (
            <Alert severity="warning">{row.studyDetailsWarning}</Alert>
          )}
          <SvbMemoBreakdown
            row={row}
            onDetails={(entryIndex) =>
              setSelectedDetails({
                ...row,
                assignments: (row.assignments || []).filter(
                  (assignment) => assignment.entryIndex === entryIndex
                ),
                selectedEntry: row.studies[entryIndex],
              })
            }
          />
        </Box>
      ),
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "queryTransfers.svb.description",
        "Description"
      ),
      width: 420,
      renderCell: ({ row }) => (
        <Box sx={{ py: 1, whiteSpace: "normal", overflowWrap: "anywhere" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {row.description}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!row.memo}
            onClick={() => setSelectedMemo(row)}
          >
            {Labels.getLabel("queryTransfers.svb.viewMemo", "View memo")}
          </Button>
        </Box>
      ),
    },
  ];

  if (!hasEmployer) {
    return (
      <Alert severity="info">
        {Labels.getLabel(
          "queryTransfers.svb.selectEmployer",
          "Select an employer to view its SVB transfers."
        )}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ my: 2 }}>
        {Labels.getLabel(
          "queryTransfers.svb.help",
          "Incoming ACH credits for this employer's configured sender account. Processed means at least one transfer was imported; MANUAL PROCESSED is a manual exclusion without transfers. Neither means funds have been applied."
        )}
      </Typography>
      <Box
        component="form"
        onSubmit={search}
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}
      >
        <TextField
          label={Labels.getLabel("queryTransfers.svb.from", "From date")}
          type="date"
          size="small"
          required
          InputLabelProps={{ shrink: true }}
          value={dates.from}
          onChange={(event) => setDates({ ...dates, from: event.target.value })}
        />
        <TextField
          label={Labels.getLabel("queryTransfers.svb.to", "To date")}
          type="date"
          size="small"
          required
          InputLabelProps={{ shrink: true }}
          value={dates.to}
          onChange={(event) => setDates({ ...dates, to: event.target.value })}
        />
        <Button type="submit" variant="outlined" disabled={isFetching}>
          {Labels.getLabel("queryTransfers.svb.search", "Search / Refresh")}
        </Button>
      </Box>
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {Labels.getLabel(
            "queryTransfers.svb.invalidDates",
            "Enter a valid date range with the from date on or before the to date."
          )}
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {Labels.getLabel(
            "queryTransfers.svb.error",
            "Error loading SVB transfers"
          )}
          {": "}
          {error?.response?.data?.description || error?.message}
        </Alert>
      )}
      <Box sx={{ height: "65vh", width: "100%" }}>
        <DataGrid
          rows={isError ? [] : data?.data || []}
          columns={columns}
          loading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{ backgroundColor: "white" }}
        />
      </Box>
      {resolution && (
        <SvbTransferResolutionDialog
          row={resolution.row}
          action={resolution.action}
          employerId={employerId}
          dates={searchDates}
          onClose={() => setResolution(null)}
        />
      )}
      {selectedDetails && (
        <Dialog
          open
          onClose={() => setSelectedDetails(null)}
          fullWidth
          maxWidth="md"
          aria-labelledby="svb-details-title"
        >
          <DialogTitle id="svb-details-title">
            {Labels.getLabel("queryTransfers.svb.moreDetails", "More details")}
          </DialogTitle>
          <DialogContent dividers>
            <Typography
              variant="body2"
              sx={{ mb: 2, overflowWrap: "anywhere" }}
            >
              {selectedDetails.id} — {selectedDetails.amount}{" "}
              {selectedDetails.currency}
            </Typography>
            {selectedDetails.selectedEntry && (
              <Typography
                variant="body2"
                sx={{ mb: 2, overflowWrap: "anywhere" }}
              >
                {selectedDetails.selectedEntry.studyId} /{" "}
                {selectedDetails.selectedEntry.invoiceNumber}
              </Typography>
            )}
            <SvbTransactionDetails row={selectedDetails} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedDetails(null)}>
              {Labels.getLabel("queryTransfers.svb.closeMemo", "Close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        open={selectedMemo !== null}
        onClose={() => setSelectedMemo(null)}
        aria-labelledby="svb-memo-dialog-title"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="svb-memo-dialog-title">
          {Labels.getLabel("queryTransfers.svb.memo", "Memo")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, overflowWrap: "anywhere" }}
          >
            {selectedMemo?.id}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontSize: "0.8rem",
            }}
          >
            {selectedMemo?.memo}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMemo(null)}>
            {Labels.getLabel("queryTransfers.svb.closeMemo", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SvbTransactionDetails({ row }) {
  return (
    <Box
      sx={{
        "& .MuiTableCell-root": {
          fontSize: "0.75rem",
          lineHeight: 1.4,
          px: 1,
          py: 0.5,
          overflowWrap: "anywhere",
        },
      }}
    >
      {row.assignmentWarning && (
        <Alert severity="error">{row.assignmentWarning}</Alert>
      )}
      {!!row.assignments?.length && (
        <Box sx={{ mb: 2, overflowX: "auto" }}>
          <Typography variant="subtitle2">
            {Labels.getLabel(
              "queryTransfers.svb.savedAssignments",
              "Saved assignments"
            )}
          </Typography>
          <Table
            size="small"
            aria-label={Labels.getLabel(
              "queryTransfers.svb.savedAssignments",
              "Saved assignments"
            )}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  {Labels.getLabel("queryTransfers.svb.invoice", "Invoice")}
                </TableCell>
                <TableCell>
                  {Labels.getLabel(
                    "queryTransfers.svb.assignedTo",
                    "Assigned to"
                  )}
                </TableCell>
                <TableCell align="right">
                  {Labels.getLabel("queryTransfers.column.amount", "Amount")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.assignments.map((assignment) => (
                <TableRow key={assignment.entryIndex}>
                  <TableCell>{assignment.invoiceNumber}</TableCell>
                  <TableCell>
                    {assignment.feeTypeId ? (
                      <>
                        {Labels.getLabel(
                          "queryTransfers.svb.administrativeFee",
                          "Administrative fee"
                        )}
                        : {assignment.feeTypeId}
                        <Typography
                          variant="caption"
                          component="div"
                          color="text.secondary"
                        >
                          {Labels.getLabel(
                            "queryTransfers.svb.feeRecord",
                            "Fee record"
                          )}{" "}
                          #{assignment.feeId} · {assignment.feeMonth}
                        </Typography>
                      </>
                    ) : (
                      <>
                        {Labels.getLabel("queryTransfers.svb.study", "Study")}:{" "}
                        {assignment.studyId}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {assignment.amount} {row.currency}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {!row.assignments?.length && !row.assignmentWarning && (
        <Typography variant="body2">
          {Labels.getLabel(
            "queryTransfers.svb.noAssignments",
            "No saved assignments are available."
          )}
        </Typography>
      )}
    </Box>
  );
}

function SvbMemoBreakdown({ row, onDetails }) {
  return (
    !!row.studies?.length && (
      <Table
        size="small"
        aria-label={Labels.getLabel(
          "queryTransfers.svb.studies",
          "Study breakdown"
        )}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              {Labels.getLabel("queryTransfers.svb.studyId", "Study ID")}
            </TableCell>
            <TableCell>
              {Labels.getLabel("queryTransfers.svb.invoice", "Invoice")}
            </TableCell>
            <TableCell align="right">
              {Labels.getLabel("queryTransfers.column.amount", "Amount")}
            </TableCell>
            <TableCell>
              {Labels.getLabel(
                "queryTransfers.svb.invoiceDate",
                "Invoice date"
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.studies.map((study, index) => (
            <TableRow key={index}>
              <TableCell>
                {study.studyId}
                {row.assignments?.some((assignment) => assignment.entryIndex === index) && (
                <Tooltip
                  title={Labels.getLabel(
                    "queryTransfers.svb.moreDetails",
                    "More details"
                  )}
                >
                  <Button
                    size="small"
                    sx={{ minWidth: 24, p: 0.25, ml: 0.5 }}
                    color={row.assignmentWarning ? "error" : "primary"}
                    aria-label={`${Labels.getLabel("queryTransfers.svb.moreDetails", "More details")} ${study.studyId || ""} (${index + 1})`}
                    onClick={() => onDetails(index)}
                  >
                    <ReceiptLongIcon fontSize="small" />
                  </Button>
                </Tooltip>
                )}
              </TableCell>
              <TableCell>{study.invoiceNumber}</TableCell>
              <TableCell align="right">
                {study.amount} {row.currency}
              </TableCell>
              <TableCell>{study.date}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  );
}
